import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Brain, Eye, Heart, Target, Zap, TrendingUp, CheckCircle, Star, BookOpen, Lightbulb, Rocket, Shield, Gift, Crown, Compass, X, Play, Pause, Volume2 } from 'lucide-react';
import { SocialProofNotification } from './components/SocialProofNotification';
import { trackLead } from './components/MetaPixel';
export function App() {
  const scrollToOffer = () => {
    const offerSection = document.getElementById('product-offer');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-b from-[#f9f5eb] to-[#f5f0e5] overflow-x-hidden" data-id="element-0">
      <div className="max-w-[1400px] mx-auto" data-id="element-1">
        <Header scrollToOffer={scrollToOffer} data-id="element-2" />
        <Introduction scrollToOffer={scrollToOffer} data-id="element-3" />
        <VisualBenefits data-id="element-4" />
        <Method scrollToOffer={scrollToOffer} data-id="element-5" />
        <WhyItWorks data-id="element-6" />
        <WhatYouLearn data-id="element-7" />
        <Results data-id="element-8" />
        <Includes data-id="element-9" />
        <ProductOffer scrollToOffer={scrollToOffer} data-id="element-10" />
        <CallToAction scrollToOffer={scrollToOffer} data-id="element-11" />
        <Footer data-id="element-12" />
      </div>
      <SocialProofNotification data-id="element-13" />
    </div>;
}
function Header({
  scrollToOffer
}: {
  scrollToOffer: () => void;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    // Cargar scripts de Wistia
    const script1 = document.createElement('script');
    script1.src = 'https://fast.wistia.com/player.js';
    script1.async = true;
    document.body.appendChild(script1);
    const script2 = document.createElement('script');
    script2.src = 'https://fast.wistia.com/embed/7hm4nbijtq.js';
    script2.async = true;
    script2.type = 'module';
    document.body.appendChild(script2);
    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <header className="w-full py-8 px-4 md:px-8 lg:px-16 text-center" data-id="element-14">
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} className="inline-block mb-4" data-id="element-15">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#e6b54a] to-[#f3c970] rounded-full flex items-center justify-center mb-4" data-id="element-16">
          <Crown className="w-8 h-8 text-[#3b1858]" data-id="element-17" />
        </div>
      </motion.div>
      <motion.h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#3b1858]" initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8
    }} data-id="element-18">
        EL SECRETO DE LA <span className="gold-text" data-id="element-19">ABUNDANCIA</span>
      </motion.h1>
      <motion.h2 className="text-xl md:text-2xl mt-4 text-[#3b1858] font-light" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8,
      delay: 0.3
    }} data-id="element-20">
        Atrae dinero, amor y éxito desde tu poder interior
      </motion.h2>

      {/* Video de Wistia */}
      <motion.div className="mt-8 max-w-3xl mx-auto" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.5
    }} data-id="element-21">
        <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-22">
          <style data-id="element-23">{`
            wistia-player[media-id='7hm4nbijtq']:not(:defined) { 
              background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/7hm4nbijtq/swatch'); 
              display: block; 
              filter: blur(5px); 
              padding-top: 56.25%; 
            }
          `}</style>
          <wistia-player media-id="7hm4nbijtq" aspect="1.7777777777777777" data-id="element-24"></wistia-player>
        </div>
      </motion.div>

      {/* Reproductor de audio */}
      <motion.div className="mt-6 max-w-md mx-auto" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.7
    }} data-id="element-25">
        <audio ref={audioRef} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} data-id="element-26">
          <source src="https://res.cloudinary.com/ddrzpyezo/video/upload/v1765825785/Voz_Preview_carta_de_ventas_xqmbes.mp3" type="audio/mpeg" data-id="element-27" />
        </audio>
      </motion.div>
    </header>;
}
function Introduction({
  scrollToOffer
}: {
  scrollToOffer: () => void;
}) {
  const methods = [{
    name: 'Afirmaciones',
    icon: '✨'
  }, {
    name: 'Vision boards',
    icon: '🎯'
  }, {
    name: 'Rituales de TikTok',
    icon: '📱'
  }, {
    name: 'Método 3-6-9',
    icon: '🔢'
  }, {
    name: 'Scripting',
    icon: '📝'
  }, {
    name: 'Meditaciones guiadas',
    icon: '🧘'
  }];
  return <section className="w-full px-4 md:px-8 lg:px-16 pt-2 pb-8 max-w-4xl mx-auto" data-id="element-28">
      <motion.div className="text-center mb-8" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} data-id="element-29">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#6b3e9e] to-[#3b1858] rounded-full flex items-center justify-center mb-4" data-id="element-30">
          <Sparkles className="w-8 h-8 text-[#e6b54a]" data-id="element-31" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-32">
          SI ESTÁS LEYENDO ESTO… NO ES CASUALIDAD
        </h2>
      </motion.div>
      <motion.div className="space-y-6 text-lg text-center" initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6
    }} data-id="element-33">
        <p className="text-xl" data-id="element-34">
          Si estás aquí, es porque llevas tiempo intentando cambiar tu vida.
        </p>
        <div className="card p-8 max-w-2xl mx-auto bg-gradient-to-br from-white to-gray-50" data-id="element-35">
          <p className="font-semibold mb-6 text-[#3b1858] text-xl" data-id="element-36">
            Y aunque haces "todo lo correcto":
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-37">
            {methods.map((method, index) => <motion.div key={index} className="inline-flex items-center gap-2 bg-gradient-to-r from-[#3b1858]/5 to-[#6b3e9e]/5 px-4 py-3 rounded-full border-2 border-[#e6b54a]/20" initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.1
          }} data-id="element-38">
                <span className="text-2xl" data-id="element-39">{method.icon}</span>
                <span className="font-medium text-[#3b1858]" data-id="element-40">
                  {method.name}
                </span>
              </motion.div>)}
          </div>
        </div>
        <p className="text-xl font-bold text-[#3b1858]" data-id="element-41">
          La verdad es que tu vida sigue igual.
        </p>

        <motion.button onClick={scrollToOffer} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.98
      }} className="bg-[#e6b54a] text-[#3b1858] font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all" data-id="element-42">
          Obtener Mi Guía Ahora
        </motion.button>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative max-w-md mx-auto my-8" data-id="element-43">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3HuJLZ7npDPgAyGAZZCTtY/PortadaLibro.png" alt="El Secreto de la Abundancia - Libro" className="w-full h-auto rounded-lg shadow-2xl" data-id="element-44" />
          <div className="absolute -inset-1 bg-[#e6b54a]/20 rounded-lg blur-md -z-10" data-id="element-45"></div>
        </motion.div>

        <div className="max-w-2xl mx-auto bg-gradient-to-r from-[#3b1858] to-[#6b3e9e] text-white p-8 rounded-2xl" data-id="element-46">
          <p className="text-2xl font-bold mb-4" data-id="element-47">
            "¿Por qué ellos sí y yo no?"
          </p>
          <p className="text-lg" data-id="element-48">
            Esta pregunta te quema por dentro cada vez que ves historias de
            éxito.
          </p>
        </div>
        <p className="text-xl font-bold text-[#3b1858]" data-id="element-49">
          No es tu culpa. No estás roto. No te falta fe.
        </p>
        <div className="card p-8 max-w-2xl mx-auto bg-gradient-to-br from-[#e6b54a]/10 to-[#f3c970]/10" data-id="element-50">
          <p className="text-2xl font-bold text-[#3b1858] mb-4" data-id="element-51">
            Lo único que te falta es:
          </p>
          <p className="text-3xl font-bold text-[#3b1858]" data-id="element-52">
            Una reprogramación interna real
          </p>
        </div>
      </motion.div>
    </section>;
}
function VisualBenefits() {
  const benefits = [{
    icon: Brain,
    title: 'No estás bloqueado',
    subtitle: 'Estás programado',
    description: 'Programado para creer que lo bueno no dura, el dinero es difícil y no mereces demasiado.',
    color: 'from-purple-500 to-purple-600'
  }, {
    icon: Eye,
    title: 'Imagina despertar diferente',
    subtitle: 'Con energía magnética',
    description: 'Una energía suave pero poderosa. Un magnetismo que nunca habías sentido antes.',
    color: 'from-[#e6b54a] to-[#f3c970]'
  }, {
    icon: Target,
    title: 'Oportunidades reales',
    subtitle: 'Puertas que se abren',
    description: 'Oportunidades laborales, personas correctas, decisiones claras y señales exactas.',
    color: 'from-[#6b3e9e] to-[#3b1858]'
  }];
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 bg-white" data-id="element-53">
      <div className="max-w-6xl mx-auto" data-id="element-54">
        <motion.div className="text-center mb-10" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-55">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-56">
            LA REALIDAD QUE NADIE TE DICE
          </h2>
          <p className="text-xl text-gray-600" data-id="element-57">(Pero tú sí sientes)</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-58">
          {benefits.map((benefit, index) => <motion.div key={index} className="text-center" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-59">
              <div className="mb-6" data-id="element-60">
                <div className={`w-20 h-20 mx-auto bg-gradient-to-br ${benefit.color} rounded-full flex items-center justify-center mb-4 shadow-lg`} data-id="element-61">
                  <benefit.icon className="w-10 h-10 text-white" data-id="element-62" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#3b1858] mb-2" data-id="element-63">
                {benefit.title}
              </h3>
              <p className="text-lg font-semibold text-[#e6b54a] mb-3" data-id="element-64">
                {benefit.subtitle}
              </p>
              <p className="text-gray-600" data-id="element-65">{benefit.description}</p>
            </motion.div>)}
        </div>
        <motion.div className="mt-10 text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-66">
          <div className="inline-block bg-gradient-to-r from-[#3b1858] to-[#6b3e9e] text-white px-8 py-4 rounded-full" data-id="element-67">
            <p className="text-xl font-bold" data-id="element-68">
              Y lo más importante: Sí se puede cambiar
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
function Method({
  scrollToOffer
}: {
  scrollToOffer: () => void;
}) {
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 max-w-6xl mx-auto" data-id="element-69">
      <motion.div className="text-center mb-10" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} data-id="element-70">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#e6b54a] to-[#f3c970] rounded-full flex items-center justify-center mb-6" data-id="element-71">
          <Rocket className="w-10 h-10 text-[#3b1858]" data-id="element-72" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-73">
          TE PRESENTO: EL SECRETO DE LA ABUNDANCIA
        </h2>
        <p className="text-xl text-gray-600" data-id="element-74">
          El método mental-energético que cambiará tu vida
        </p>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-75">
        {[{
        icon: Brain,
        text: 'Limpiar creencias limitantes'
      }, {
        icon: Zap,
        text: 'Activar tu frecuencia interna'
      }, {
        icon: Target,
        text: 'Usar métodos virales correctamente'
      }, {
        icon: Eye,
        text: 'Visualizar con técnica científica'
      }, {
        icon: Compass,
        text: 'Alinear decisiones con tus deseos'
      }, {
        icon: TrendingUp,
        text: 'Abrir puertas que antes no veías'
      }].map((item, index) => <motion.div key={index} className="card p-6 flex items-center gap-4" initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: index * 0.1
      }} data-id="element-76">
            <div className="w-12 h-12 bg-gradient-to-br from-[#6b3e9e] to-[#3b1858] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-77">
              <item.icon className="w-6 h-6 text-[#e6b54a]" data-id="element-78" />
            </div>
            <p className="text-lg font-semibold text-[#3b1858]" data-id="element-79">{item.text}</p>
          </motion.div>)}
      </div>
      <motion.div className="mt-10 card p-8 bg-gradient-to-br from-[#e6b54a]/10 to-[#f3c970]/10 text-center" initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} data-id="element-80">
        <p className="text-2xl font-bold text-[#3b1858]" data-id="element-81">
          La mezcla perfecta entre mente, energía y acción
        </p>
      </motion.div>
      <motion.div className="mt-8 text-center" initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.2
    }} data-id="element-82">
        <motion.button onClick={scrollToOffer} className="bg-[#e6b54a] text-[#3b1858] font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.98
      }} data-id="element-83">
          Obtener Mi Guía Ahora
        </motion.button>
      </motion.div>
    </section>;
}
function WhyItWorks() {
  const formula = [{
    icon: Brain,
    title: 'Mente reprogramada',
    description: 'Creencias nuevas producen resultados nuevos'
  }, {
    icon: Zap,
    title: 'Energía activada',
    description: 'Emoción, impulso, atracción y claridad en coherencia'
  }, {
    icon: Eye,
    title: 'Visualización científica',
    description: 'Tu mente necesita imágenes claras y sostenidas'
  }, {
    icon: Heart,
    title: 'Afirmaciones somáticas',
    description: 'Tu cuerpo debe creer lo que dices'
  }, {
    icon: Target,
    title: 'Acción inspirada',
    description: 'Pequeños pasos que abren grandes puertas'
  }];
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 bg-white" data-id="element-84">
      <div className="max-w-6xl mx-auto" data-id="element-85">
        <motion.div className="text-center mb-10" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-86">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-87">
            LA FÓRMULA COMPLETA
          </h2>
          <p className="text-xl text-gray-600" data-id="element-88">
            Por qué nunca te ha funcionado manifestar (y ahora sí)
          </p>
        </motion.div>
        <div className="space-y-6" data-id="element-89">
          {formula.map((item, index) => <motion.div key={index} className="card p-6 flex items-start gap-6" initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-90">
              <div className="w-16 h-16 bg-gradient-to-br from-[#e6b54a] to-[#f3c970] rounded-2xl flex items-center justify-center flex-shrink-0" data-id="element-91">
                <item.icon className="w-8 h-8 text-[#3b1858]" data-id="element-92" />
              </div>
              <div data-id="element-93">
                <h3 className="text-xl font-bold text-[#3b1858] mb-2" data-id="element-94">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-95">{item.description}</p>
              </div>
            </motion.div>)}
        </div>
        <motion.div className="mt-10 text-center bg-gradient-to-r from-[#3b1858] to-[#6b3e9e] text-white p-8 rounded-2xl" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-96">
          <p className="text-2xl font-bold" data-id="element-97">
            Si te falta una, no funciona. Por eso antes no servía. Ahora sí.
          </p>
        </motion.div>
      </div>
    </section>;
}
function WhatYouLearn() {
  const lessons = [{
    icon: Lightbulb,
    number: '1',
    title: 'Descubre tu poder',
    content: 'Identificarás y eliminarás creencias heredadas. Las reemplazarás por creencias expansivas que atraen abundancia.'
  }, {
    icon: Eye,
    number: '2',
    title: 'Visualiza para materializar',
    content: 'Aprenderás la técnica usada por atletas olímpicos: emoción dirigida, visión clara e instrucciones al subconsciente.'
  }, {
    icon: Sparkles,
    number: '3',
    title: 'Afirmaciones 24/7',
    content: 'Métodos virales explicados desde la psicología: 3-6-9, 55x5, scripting consciente y afirmaciones cuánticas.'
  }, {
    icon: Target,
    number: '4',
    title: 'Acción inspirada',
    content: 'Aprenderás a reconocer señales, intuición y oportunidades. El universo abre puertas, tú caminas hacia ellas.'
  }, {
    icon: Shield,
    number: '5',
    title: 'Manifestar en tu realidad',
    content: 'Cómo manifestar con presión económica, lidiar con burlas del entorno y usar cualquier situación como ventaja.'
  }];
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 max-w-6xl mx-auto" data-id="element-98">
      <motion.div className="text-center mb-10" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} data-id="element-99">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#6b3e9e] to-[#3b1858] rounded-full flex items-center justify-center mb-6" data-id="element-100">
          <BookOpen className="w-10 h-10 text-[#e6b54a]" data-id="element-101" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-102">
          LO QUE VAS A APRENDER
        </h2>
        <p className="text-xl text-gray-600" data-id="element-103">(Y cómo cambia tu vida)</p>
      </motion.div>
      <div className="space-y-6" data-id="element-104">
        {lessons.map((lesson, index) => <motion.div key={index} className="card p-8" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: index * 0.1
      }} data-id="element-105">
            <div className="flex items-start gap-6" data-id="element-106">
              <div className="flex-shrink-0" data-id="element-107">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e6b54a] to-[#f3c970] rounded-2xl flex items-center justify-center mb-3" data-id="element-108">
                  <lesson.icon className="w-8 h-8 text-[#3b1858]" data-id="element-109" />
                </div>
                <div className="w-16 h-16 bg-[#3b1858] rounded-full flex items-center justify-center" data-id="element-110">
                  <span className="text-2xl font-bold text-[#e6b54a]" data-id="element-111">
                    {lesson.number}
                  </span>
                </div>
              </div>
              <div className="flex-1" data-id="element-112">
                <h3 className="text-2xl font-bold text-[#3b1858] mb-3" data-id="element-113">
                  {lesson.title}
                </h3>
                <p className="text-lg text-gray-600" data-id="element-114">{lesson.content}</p>
              </div>
            </div>
          </motion.div>)}
      </div>
    </section>;
}
function Results() {
  const results = ['Renueva tu energía', 'Te vuelves más atractivo emocionalmente', 'Dejas de atraer drama', 'Las personas correctas llegan', 'Tu intuición se activa', 'Tienes claridad para tomar decisiones', 'El dinero fluye', 'Llegan oportunidades sin esfuerzo', 'Te vuelves magnético'];
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 bg-gradient-to-br from-[#3b1858] to-[#6b3e9e]" data-id="element-115">
      <div className="max-w-4xl mx-auto text-center" data-id="element-116">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} data-id="element-117">
          <div className="w-20 h-20 mx-auto bg-[#e6b54a] rounded-full flex items-center justify-center mb-6" data-id="element-118">
            <Star className="w-10 h-10 text-[#3b1858]" data-id="element-119" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-120">
            LO QUE EMPIEZA A PASAR
          </h2>
          <p className="text-xl text-[#e6b54a] mb-10" data-id="element-121">
            Cuando aplicas el método
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-4" data-id="element-122">
          {results.map((result, index) => <motion.div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl flex items-center gap-4" initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          delay: index * 0.05
        }} data-id="element-123">
              <CheckCircle className="w-6 h-6 text-[#e6b54a] flex-shrink-0" data-id="element-124" />
              <p className="text-white text-left font-medium" data-id="element-125">{result}</p>
            </motion.div>)}
        </div>
        <motion.div className="mt-10 bg-[#e6b54a] text-[#3b1858] px-8 py-6 rounded-2xl inline-block" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-126">
          <p className="text-2xl font-bold" data-id="element-127">
            Y lo mejor: todo comienza antes de los 7 días
          </p>
        </motion.div>
      </div>
    </section>;
}
function Includes() {
  const includes = [{
    icon: Brain,
    text: 'Reprogramación mental guiada'
  }, {
    icon: Eye,
    text: 'Técnicas científicas de visualización'
  }, {
    icon: Target,
    text: 'Ejercicios diarios'
  }, {
    icon: Zap,
    text: 'Activación energética real'
  }, {
    icon: Shield,
    text: 'Limpieza de creencias limitantes'
  }, {
    icon: Compass,
    text: 'Guía de acción inspirada'
  }, {
    icon: Heart,
    text: 'Adaptado a tu realidad'
  }, {
    icon: Gift,
    text: 'Material descargable desde el día uno'
  }];
  return <section className="w-full px-4 md:px-8 lg:px-16 pt-12 pb-6 max-w-6xl mx-auto" data-id="element-128">
      <motion.div className="text-center mb-10" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} data-id="element-129">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#e6b54a] to-[#f3c970] rounded-full flex items-center justify-center mb-6" data-id="element-130">
          <Gift className="w-10 h-10 text-[#3b1858]" data-id="element-131" />
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#3b1858] mb-4" data-id="element-132">
          QUÉ INCLUYE
        </h2>
        <p className="text-xl text-gray-600" data-id="element-133">El Secreto de la Abundancia</p>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-134">
        {includes.map((item, index) => <motion.div key={index} className="card p-6 flex items-center gap-4" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: index * 0.05
      }} data-id="element-135">
            <div className="w-14 h-14 bg-gradient-to-br from-[#6b3e9e] to-[#3b1858] rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-136">
              <item.icon className="w-7 h-7 text-[#e6b54a]" data-id="element-137" />
            </div>
            <p className="text-lg font-semibold text-[#3b1858]" data-id="element-138">{item.text}</p>
          </motion.div>)}
      </div>
    </section>;
}
function ProductOffer({
  scrollToOffer
}: {
  scrollToOffer: () => void;
}) {
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [showAllBonus2Benefits, setShowAllBonus2Benefits] = useState(false);
  const allBenefits = ['La ciencia real detrás del 3-6-9 (neuroplasticidad + RAS)', 'Cómo evitar los bloqueos que arruinan tu manifestación', 'Cómo repetir tus afirmaciones con emoción verdadera', 'Cómo elegir frases que tu mente sí cree', 'Cuándo descansar para no sabotear tu progreso', 'Los 5 errores más comunes que destruyen resultados', 'Una rutina 3-6-9 lista para aplicar desde hoy'];
  const visibleBenefits = showAllBenefits ? allBenefits : allBenefits.slice(0, 2);
  const allBonus2Benefits = ['Cómo manifestar con presión económica sin caer en ansiedad', 'Cómo proteger tu energía de la crítica familiar y del entorno negativo', 'Cómo atraer dinero viviendo paycheck-to-paycheck', 'Cómo elevar tu energía cuando tu casa es caótica o desgastante', 'Métodos prácticos: Micrologros diarios, 3 formas de recibir, Ritual AM/PM de 2 minutos', 'Refugio energético y Canción ancla', 'Ejemplos reales de manifestación en la vida cotidiana'];
  const visibleBonus2Benefits = showAllBonus2Benefits ? allBonus2Benefits : allBonus2Benefits.slice(0, 2);
  const handlePurchaseClick = () => {
    trackLead();
    window.open('https://pay.hotmart.com/X103396152N', '_blank');
  };
  return <section id="product-offer" className="w-full px-4 md:px-8 lg:px-16 pt-6 pb-16 bg-gradient-to-br from-[#3b1858] to-[#6b3e9e]" data-id="element-139">
      <div className="max-w-6xl mx-auto" data-id="element-140">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-141">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-142">
            ACCESO INMEDIATO A TU TRANSFORMACIÓN
          </h2>
          <p className="text-xl text-[#e6b54a]" data-id="element-143">
            Disponible en formato digital desde cualquier dispositivo
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-6 items-start" data-id="element-144">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-145">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nFGKNg4hMryeamDcYPKgxA/Oferta_-_Mockup.png" alt="El Secreto de la Abundancia - Disponible en todos tus dispositivos" className="w-full h-auto rounded-2xl shadow-2xl" data-id="element-146" />
          </motion.div>
          <motion.div className="space-y-8" initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-147">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl" data-id="element-148">
              <div className="mb-2" data-id="element-149">
                <span className="text-2xl text-white/60 line-through" data-id="element-150">
                  $15.94 USD
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-4" data-id="element-151">
                <span className="text-5xl md:text-6xl font-bold text-[#e6b54a]" data-id="element-152">
                  $7.97
                </span>
                <span className="text-2xl text-white" data-id="element-153">USD</span>
              </div>
              <p className="text-white text-lg mb-6" data-id="element-154">
                Precio especial de lanzamiento
              </p>
              <div className="space-y-3 mb-8" data-id="element-155">
                {['Acceso inmediato después de la compra', 'Compatible con todos tus dispositivos', 'Lee desde móvil, tablet o computadora', 'Sin costos adicionales ni suscripciones'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-156">
                    <CheckCircle className="w-5 h-5 text-[#e6b54a] flex-shrink-0" data-id="element-157" />
                    <span className="text-white" data-id="element-158">{item}</span>
                  </div>)}
              </div>

              {/* BONO 1 */}
              <motion.div className="bg-gradient-to-br from-[#e6b54a] to-[#f3c970] p-5 rounded-xl mb-6" initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} data-id="element-159">
                <div className="flex items-center justify-between mb-3" data-id="element-160">
                  <div data-id="element-161">
                    <p className="text-[#3b1858] font-bold text-lg mb-1" data-id="element-162">
                      🎁 BONO 1 - GRATIS
                    </p>
                    <p className="text-[#3b1858] font-semibold text-base mb-1" data-id="element-163">
                      Guía 3-6-9 Correcta
                    </p>
                    <p className="text-[#3b1858]/80 text-xs" data-id="element-164">
                      Incluido con tu compra
                    </p>
                  </div>
                  <div className="text-right" data-id="element-165">
                    <p className="text-[#3b1858] line-through text-lg font-semibold" data-id="element-166">
                      $19
                    </p>
                    <p className="text-[#3b1858] font-bold text-xl" data-id="element-167">GRATIS</p>
                  </div>
                </div>
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6mB6EVYeoyZe5Duu35WUeU/BONO1_-_Mockup.png" alt="Guía 3-6-9 Correcta - Bono Gratis Incluido" className="w-full h-auto rounded-lg shadow-lg mb-3" data-id="element-168" />
                <div className="bg-[#3b1858]/10 rounded-lg p-4 mb-3" data-id="element-169">
                  <p className="text-[#3b1858] font-bold text-center mb-2 text-sm" data-id="element-170">
                    El método definitivo para manifestar sin autosabotearte
                  </p>
                  <p className="text-[#3b1858]/90 text-xs text-center mb-3" data-id="element-171">
                    Descubre cómo usar el famoso método 3-6-9 de forma correcta,
                    sin ansiedad, sin confusión y sin repetir afirmaciones
                    "vacías" que no funcionan.
                  </p>
                  <div className="space-y-1.5" data-id="element-172">
                    {visibleBenefits.map((benefit, index) => <motion.div key={index} className="flex items-start gap-2 text-[#3b1858]" initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: index * 0.05
                  }} data-id="element-173">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#3b1858]" data-id="element-174" />
                        <span className="text-xs" data-id="element-175">{benefit}</span>
                      </motion.div>)}
                  </div>
                  <button onClick={() => setShowAllBenefits(!showAllBenefits)} className="w-full mt-3 text-[#3b1858] font-semibold text-xs py-2 px-3 rounded-lg bg-[#3b1858]/5 hover:bg-[#3b1858]/10 transition-colors flex items-center justify-center gap-2" data-id="element-176">
                    {showAllBenefits ? 'Ver menos' : 'Ver más'}
                    <motion.span animate={{
                    rotate: showAllBenefits ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-177">
                      ▼
                    </motion.span>
                  </button>
                </div>
                <div className="bg-[#3b1858] rounded-lg p-3" data-id="element-178">
                  <p className="text-[#e6b54a] font-semibold text-center text-xs" data-id="element-179">
                    No teoría. Solo lo que SÍ funciona en tu realidad. Desde
                    HOY.
                  </p>
                </div>
              </motion.div>

              {/* BONO 2 */}
              <motion.div className="bg-gradient-to-br from-[#e6b54a] to-[#f3c970] p-5 rounded-xl mb-8" initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} data-id="element-180">
                <div className="flex items-center justify-between mb-3" data-id="element-181">
                  <div data-id="element-182">
                    <p className="text-[#3b1858] font-bold text-lg mb-1" data-id="element-183">
                      ✨ BONO 2 - GRATIS
                    </p>
                    <p className="text-[#3b1858] font-semibold text-base mb-1" data-id="element-184">
                      Guía de Manifestación (Edición Especial)
                    </p>
                    <p className="text-[#3b1858]/80 text-xs" data-id="element-185">
                      Incluido con tu compra
                    </p>
                  </div>
                  <div className="text-right" data-id="element-186">
                    <p className="text-[#3b1858] line-through text-lg font-semibold" data-id="element-187">
                      $19
                    </p>
                    <p className="text-[#3b1858] font-bold text-xl" data-id="element-188">GRATIS</p>
                  </div>
                </div>
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uVHdY1JedmFNMk5r8ZkPiB/BONO2_-_Mockup.png" alt="Guía de Manifestación Edición Especial - Bono Gratis Incluido" className="w-full h-auto rounded-lg shadow-lg mb-3" data-id="element-189" />
                <div className="bg-[#3b1858]/10 rounded-lg p-4 mb-3" data-id="element-190">
                  <p className="text-[#3b1858] font-bold text-center mb-2 text-sm" data-id="element-191">
                    La guía que enseña lo que NADIE te explica sobre manifestar
                    en la vida real
                  </p>
                  <p className="text-[#3b1858]/90 text-xs text-center mb-3" data-id="element-192">
                    Este bono revela cómo manifestar cuando la vida no es
                    perfecta, cuando hay presión económica, críticas familiares,
                    estrés diario y poco tiempo. Es una guía realista, diseñada
                    para personas que viven al día y aun así quieren atraer
                    abundancia.
                  </p>
                  <div className="space-y-1.5" data-id="element-193">
                    {visibleBonus2Benefits.map((benefit, index) => <motion.div key={index} className="flex items-start gap-2 text-[#3b1858]" initial={{
                    opacity: 0,
                    y: -10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: index * 0.05
                  }} data-id="element-194">
                        <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-[#3b1858]" data-id="element-195" />
                        <span className="text-xs" data-id="element-196">{benefit}</span>
                      </motion.div>)}
                  </div>
                  <button onClick={() => setShowAllBonus2Benefits(!showAllBonus2Benefits)} className="w-full mt-3 text-[#3b1858] font-semibold text-xs py-2 px-3 rounded-lg bg-[#3b1858]/5 hover:bg-[#3b1858]/10 transition-colors flex items-center justify-center gap-2" data-id="element-197">
                    {showAllBonus2Benefits ? 'Ver menos' : 'Ver más'}
                    <motion.span animate={{
                    rotate: showAllBonus2Benefits ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-198">
                      ▼
                    </motion.span>
                  </button>
                </div>
                <div className="bg-[#3b1858] rounded-lg p-3" data-id="element-199">
                  <p className="text-[#e6b54a] font-semibold text-center text-xs" data-id="element-200">
                    Funciona porque es práctico. Lo aplicas HOY, incluso en días
                    difíciles.
                  </p>
                </div>
              </motion.div>

              <motion.button onClick={handlePurchaseClick} className="w-full bg-[#e6b54a] text-[#3b1858] font-bold text-xl px-8 py-5 rounded-xl shadow-lg hover:shadow-xl transition-all" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-201">
                Comprar Ahora
              </motion.button>
            </div>

            <div className="bg-[#e6b54a]/20 backdrop-blur-sm p-6 rounded-xl border-2 border-[#e6b54a]/30" data-id="element-202">
              <div className="flex items-center gap-3 mb-3" data-id="element-203">
                <Shield className="w-6 h-6 text-[#e6b54a]" data-id="element-204" />
                <p className="text-white font-semibold" data-id="element-205">Compra 100% Segura</p>
              </div>
              <p className="text-white/80 text-sm" data-id="element-206">
                Tu información está protegida con encriptación de nivel bancario
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CallToAction({
  scrollToOffer
}: {
  scrollToOffer: () => void;
}) {
  return <section className="w-full px-4 md:px-8 lg:px-16 py-12 bg-gradient-to-br from-[#e6b54a] to-[#f3c970]" data-id="element-207">
      <div className="max-w-4xl mx-auto text-center" data-id="element-208">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-209">
          <div className="w-24 h-24 mx-auto bg-[#3b1858] rounded-full flex items-center justify-center mb-8" data-id="element-210">
            <Rocket className="w-12 h-12 text-[#e6b54a]" data-id="element-211" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#3b1858] mb-8" data-id="element-212">
            TU NUEVA VIDA EMPIEZA HOY
          </h2>
          <div className="card p-8 mb-8" data-id="element-213">
            <p className="text-xl text-gray-700 mb-6" data-id="element-214">Si quieres:</p>
            <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-215">
              {['Más dinero', 'Amor sano', 'Un glow-up interno', 'Intuición activada', 'Oportunidades reales', 'Paz mental', 'Energía limpia', 'Metas cumplidas'].map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-216">
                  <Star className="w-5 h-5 text-[#e6b54a]" data-id="element-217" />
                  <span className="text-lg font-medium text-[#3b1858]" data-id="element-218">
                    {item}
                  </span>
                </div>)}
            </div>
            <p className="text-2xl font-bold text-[#3b1858]" data-id="element-219">
              No necesitas motivación. Necesitas El Secreto de la Abundancia.
            </p>
          </div>
          <motion.button onClick={scrollToOffer} className="bg-gradient-to-r from-[#3b1858] to-[#6b3e9e] text-[#e6b54a] font-bold text-xl md:text-2xl px-12 py-6 mb-8 rounded-xl shadow-lg hover:shadow-xl transition-all" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.98
        }} data-id="element-220">
            Haz clic aquí y descárgalo ahora mismo
          </motion.button>
          <div className="bg-[#3b1858] text-white p-8 rounded-2xl" data-id="element-221">
            <p className="text-2xl font-bold mb-4" data-id="element-222">
              Tu nueva vida empieza hoy.
            </p>
            <p className="text-xl" data-id="element-223">No mañana. No cuando "puedas".</p>
            <p className="text-4xl font-bold mt-4 text-[#e6b54a]" data-id="element-224">Hoy.</p>
          </div>
        </motion.div>
      </div>
    </section>;
}
function Footer() {
  return <footer className="w-full px-4 md:px-8 lg:px-16 py-8 bg-[#3b1858] text-center" data-id="element-225">
      <p className="text-[#e6b54a]" data-id="element-226">
        © 2023 El Secreto de la Abundancia. Todos los derechos reservados.
      </p>
    </footer>;
}